// Barra de KPIs de la flota MDM: la foto que el operador lee antes de tocar un filtro.
// Cuenta cada proveedor a propósito: un proveedor sin equipos es un cero, no un hueco.
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import { KpiBar, type KpiItem } from '@/shared/patterns/KpiBar';
import {
    DEVICES_NOUN,
    loadedCountLabel,
    loadedCountTitle,
    type LoadedScope,
} from '@/shared/patterns/LoadedScope';
import { MDM_PROVIDER_LABEL, NO_VALUE, deviceCountLabel } from '../logic';
import type { MdmFleetSummary } from '../types';

/** Un cero no se pinta de color: solo tiñe la cuenta que de verdad pide atención. */
function toneWhenAny(count: number, tone: KpiItem['tone']): KpiItem['tone'] {
    return count > 0 ? tone : 'default';
}

/** «Trustonic 0 · Datacultr 5 · Motosafe 1»: de quién es cada equipo, en el orden del enum. */
function providerSplit(summary: MdmFleetSummary): string {
    return Object.values(MdmProviderEnum)
        .map(
            (provider) => `${MDM_PROVIDER_LABEL[provider]} ${String(summary.byProvider[provider])}`,
        )
        .join(' · ');
}

/** Los equipos callados y los que nunca hablaron son dos problemas distintos: el sub los separa. */
function silenceSub(summary: MdmFleetSummary): string {
    return summary.neverReported > 0
        ? `${String(summary.neverReported)} nunca reportó`
        : 'más de 7 días';
}

/** «2 programados · 1 sin confirmar»: lo que no es candado confirmado, sin esconderlo. */
function lockOrdersSub(summary: MdmFleetSummary): string | undefined {
    const { scheduled, unconfirmed, refuted, cutoffPassed, cutoffRefuted } =
        summary.lockOrdersByPhase;
    const parts = [
        scheduled > 0
            ? `${String(scheduled)} ${scheduled === 1 ? 'programado' : 'programados'}`
            : null,
        unconfirmed > 0 ? `${String(unconfirmed)} sin confirmar` : null,
        refuted > 0 ? `${String(refuted)} ${refuted === 1 ? 'desmentido' : 'desmentidos'}` : null,
        cutoffPassed > 0
            ? `${String(cutoffPassed)} ${cutoffPassed === 1 ? 'corte vencido' : 'cortes vencidos'}`
            : null,
        cutoffRefuted > 0
            ? `${String(cutoffRefuted)} ${cutoffRefuted === 1 ? 'corte desmentido' : 'cortes desmentidos'}`
            : null,
    ].filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join(' · ') : undefined;
}

const LOCKED_NOTE =
    'Solo los candados que el proveedor confirmó: es lo mismo que el filtro «Bloqueado».';

const ATTENTION_NOTE =
    'Equipos con alertas vivas o con el enrolamiento trabado: es lo mismo que el filtro «Requiere atención».';

const NOT_REQUESTED_NOTE =
    'Con el filtro «Estado MDM» puesto solo se piden los equipos de ese estado, así que este conteo no se puede dar. Quita el filtro para verlo.';

const READ_FAILED_NOTE =
    'No se pudo leer ese estado, así que este conteo no se puede dar. Vuelve a pedirlo con el botón del pie de la tabla.';

const FLEET_READ_FAILED_NOTE =
    'Falta un estado entero de la flota, así que este conteo no se puede dar. Vuelve a pedirlo con el botón del pie de la tabla.';

/** Sin estados caídos: referencia estable para el valor por omisión de la prop. */
const NO_FAILED_STATUSES: readonly LoanDeviceMdmStatusEnum[] = [];

/** Un KPI y el estado del que sale; sin `needs`, cuenta sobre la flota entera. */
type FleetKpi = KpiItem & { needs?: LoanDeviceMdmStatusEnum };

/** Un KPI que no se puede dar: «—» y el porqué, nunca un cero que parezca dato. */
function unavailable(item: KpiItem, note: string): KpiItem {
    return { label: item.label, value: NO_VALUE, tone: 'default', title: note };
}

/**
 * Mientras falten páginas ningún KPI es un total: el número se vuelve «12 de 75» y el tooltip dice
 * sobre qué cuenta (ESTANDAR-TABLAS §5.4). `statuses` = los estados pedidos; `null`, todos.
 */
export function buildFleetKpis(
    summary: MdmFleetSummary,
    scope: LoadedScope | null,
    statuses: readonly LoanDeviceMdmStatusEnum[] | null = null,
    failedStatuses: readonly LoanDeviceMdmStatusEnum[] = NO_FAILED_STATUSES,
): KpiItem[] {
    const value = (count: number): string =>
        scope === null ? String(count) : loadedCountLabel(count, scope);
    const note = (base?: string): string | undefined =>
        scope === null ? base : loadedCountTitle(base, scope, DEVICES_NOUN);
    const counted = (needs: LoanDeviceMdmStatusEnum | undefined): boolean =>
        statuses === null || (needs !== undefined && statuses.includes(needs));
    // Un estado entero ausente tampoco deja contar sobre la flota: los KPIs sin `needs` la miran
    // toda, así que «Equipos» o «Atención» estarían afirmando sobre una lista a la que le falta.
    const readable = (needs: LoanDeviceMdmStatusEnum | undefined): boolean =>
        needs === undefined ? failedStatuses.length === 0 : !failedStatuses.includes(needs);

    const items: FleetKpi[] = [
        {
            label: 'Equipos',
            value: value(summary.total),
            sub: providerSplit(summary),
            title: note(),
        },
        {
            label: 'Activos',
            needs: LoanDeviceMdmStatusEnum.ACTIVE,
            value: value(summary.active),
            sub:
                summary.activeUnconfirmed > 0
                    ? `${String(summary.activeUnconfirmed)} sin confirmar`
                    : undefined,
            tone: toneWhenAny(summary.active, 'success'),
            title: note(),
        },
        {
            label: 'Bloqueados',
            needs: LoanDeviceMdmStatusEnum.LOCKED,
            value: value(summary.locked),
            sub: lockOrdersSub(summary),
            tone: toneWhenAny(summary.locked, 'destructive'),
            title: note(LOCKED_NOTE),
        },
        {
            label: 'Enrolando',
            needs: LoanDeviceMdmStatusEnum.ENROLLING,
            value: value(summary.enrolling),
            tone: toneWhenAny(summary.enrolling, 'info'),
            title: note(),
        },
        {
            label: 'Atención',
            value: value(summary.needsAttention),
            tone: toneWhenAny(summary.needsAttention, 'warning'),
            title: note(ATTENTION_NOTE),
        },
        {
            label: 'Sin señal',
            value: value(summary.silent),
            sub: silenceSub(summary),
            tone: toneWhenAny(summary.silent, 'warning'),
            title: note(),
        },
        {
            label: 'Ops en vuelo',
            value: value(summary.pendingOperations),
            sub: `en ${deviceCountLabel(summary.devicesWithPendingOperations)}`,
            tone: toneWhenAny(summary.pendingOperations, 'info'),
            title: note(),
        },
    ];
    return items.map(({ needs, ...item }) => {
        if (!counted(needs)) return unavailable(item, NOT_REQUESTED_NOTE);
        if (readable(needs)) return item;
        return unavailable(item, needs === undefined ? FLEET_READ_FAILED_NOTE : READ_FAILED_NOTE);
    });
}

export function MdmFleetKpis({
    summary,
    scope = null,
    statuses = null,
    failedStatuses = NO_FAILED_STATUSES,
}: {
    summary: MdmFleetSummary;
    /** Cuánto se lleva cargado y si quedan páginas. `null` = la flota entera está en mano. */
    scope?: LoadedScope | null;
    /** Los `mdmStatus` pedidos al servidor. `null` = todos. */
    statuses?: readonly LoanDeviceMdmStatusEnum[] | null;
    /** Los `mdmStatus` que se pidieron y no contestaron: sus conteos no existen. */
    failedStatuses?: readonly LoanDeviceMdmStatusEnum[];
}) {
    return <KpiBar items={buildFleetKpis(summary, scope, statuses, failedStatuses)} />;
}
